import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEnquiries, getProductEnquiries } from '../../api';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();

  const [contactStats, setContactStats] = useState({ total: 0, unread: 0, replied: 0, pending: 0 });
  const [productStats, setProductStats] = useState({ total: 0, unread: 0, replied: 0, pending: 0 });
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openCardId, setOpenCardId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [contacts, products] = await Promise.all([
          getEnquiries(),
          getProductEnquiries(),
        ]);

        if (Array.isArray(contacts)) {
          setContactStats({
            total: contacts.length,
            unread: contacts.filter((c) => !c.is_read).length,
            replied: contacts.filter((c) => c.is_replied).length,
            pending: contacts.filter((c) => !c.is_replied).length,
          });
          setRecentContacts(
            [...contacts]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .slice(0, 5)
          );
        }

        if (Array.isArray(products)) {
          setProductStats({
            total: products.length,
            unread: products.filter((c) => !c.is_read).length,
            replied: products.filter((c) => c.is_replied).length,
            pending: products.filter((c) => !c.is_replied).length,
          });
          setRecentProducts(
            [...products]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .slice(0, 5)
          );
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div className="d-dashboard-container">
        <h1>Dashboard</h1>
        <p className="d-welcome">Loading...</p>
      </div>
    );
  }

  return (
    <div className="d-dashboard-container">
      <h1>
          <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 10.5L12 3l9 7.5" />
            <path d="M5 9.5V21h14V9.5" />
            <path d="M9 21v-6h6v6" />
          </svg>
          Dashboard
      </h1>
      <p className="d-welcome">Welcome back, Admin!</p>

      {/* Stats Cards */}
      <div className="d-stats-grid">
        <div className="d-stat-card">
          <span className="d-stat-icon">▣</span>
          <div>
            <p className="d-stat-label">Contact Enquiries</p>
            <p className="d-stat-value">{contactStats.total}</p>
          </div>
        </div>
        <div className="d-stat-card">
          <span className="d-stat-icon">◈</span>
          <div>
            <p className="d-stat-label">Product Enquiries</p>
            <p className="d-stat-value">{productStats.total}</p>
          </div>
        </div>
        <div className="d-stat-card">
          <span className="d-stat-icon">◷</span>
          <div>
            <p className="d-stat-label">Pending Contact</p>
            <p className="d-stat-value">{contactStats.pending}</p>
          </div>
        </div>
        <div className="d-stat-card">
          <span className="d-stat-icon">✓</span>
          <div>
            <p className="d-stat-label">Replied Contact</p>
            <p className="d-stat-value">{contactStats.replied}</p>
          </div>
        </div>
      </div>

      {/* Recent Contact Enquiries */}
      <div className="d-table-section">
        <div className="d-table-header">
          <h3>Recent Contact Enquiries</h3>
          <button className="d-more-btn" onClick={() => navigate('/admin/contact')}>
            More →
          </button>
        </div>

        <table className="d-dash-desktop-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>SERVICE</th>
              <th>DATE</th>
            </tr>
          </thead>
          <tbody>
            {recentContacts.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  No contact enquiries yet
                </td>
              </tr>
            ) : (
              recentContacts.map((c) => (
                <tr key={c.id}>
                  <td title={c.name}>
                    {c.name}
                    {!c.is_read && <span className="d-new-badge">NEW</span>}
                  </td>
                  <td title={c.email}>{c.email}</td>
                  <td>{c.phone}</td>
                  <td title={c.service}>{c.service}</td>
                  <td>{formatDate(c.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="d-dash-mobile-cards">
          {recentContacts.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '20px' }}>No enquiries yet</p>
          ) : (
            recentContacts.map((c) => {
              const isOpen = openCardId === `c-${c.id}`;
              return (
                <div key={c.id} className="d-dash-mobile-card">
                  <div className="d-dash-mobile-card-header" onClick={() => setOpenCardId(isOpen ? null : `c-${c.id}`)}>
                    <div className="d-dash-mobile-card-title">
                      <strong>{c.name}</strong>
                      {!c.is_read && <span className="d-new-badge">NEW</span>}
                    </div>
                    <div className="d-dash-mobile-card-right">
                      <span className="d-dash-mobile-date">{formatDate(c.created_at)}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="d-dash-mobile-card-body">
                      <p><b>Email:</b> {c.email}</p>
                      <p><b>Phone:</b> {c.phone}</p>
                      <p><b>Service:</b> {c.service}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Product Enquiries */}
      <div className="d-table-section">
        <div className="d-table-header">
          <h3>Recent Product Enquiries</h3>
          <button className="d-more-btn" onClick={() => navigate('/admin/product-enquiries')}>
            More →
          </button>
        </div>

        <table className="d-dash-desktop-table">
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>DATE</th>
            </tr>
          </thead>
          <tbody>
            {recentProducts.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  No product enquiries yet
                </td>
              </tr>
            ) : (
              recentProducts.map((p) => (
                <tr key={p.id}>
                  <td><span className="d-product-badge">{p.product || 'Other'}</span></td>
                  <td title={p.name}>
                    {p.name}
                    {!p.is_read && <span className="d-new-badge">NEW</span>}
                  </td>
                  <td title={p.email}>{p.email}</td>
                  <td>{p.phone}</td>
                  <td>{formatDate(p.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="d-dash-mobile-cards">
          {recentProducts.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '20px' }}>No enquiries yet</p>
          ) : (
            recentProducts.map((p) => {
              const isOpen = openCardId === `p-${p.id}`;
              return (
                <div key={p.id} className="d-dash-mobile-card">
                  <div className="d-dash-mobile-card-header" onClick={() => setOpenCardId(isOpen ? null : `p-${p.id}`)}>
                    <div className="d-dash-mobile-card-title">
                      <strong>{p.name}</strong>
                      {!p.is_read && <span className="d-new-badge">NEW</span>}
                    </div>
                    <div className="d-dash-mobile-card-right">
                      <span className="d-dash-mobile-date">{formatDate(p.created_at)}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="d-dash-mobile-card-body">
                      <p><b>Product:</b> <span className="d-product-badge">{p.product || 'Other'}</span></p>
                      <p><b>Email:</b> {p.email}</p>
                      <p><b>Phone:</b> {p.phone}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;